import { useEffect, useRef, useState } from "react";
import { ViewerHost } from "../ViewerHost";
import type { ViewerClient } from "../../client/ViewerClient";
import { getModelGlbUrl, saveModelPoster, type VaultModel } from "../../features/vault/vaultApi";

function blobToDataURL(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result as string);
    r.onerror = reject;
    r.readAsDataURL(blob);
  });
}

/**
 * Background thumbnailer for the Drive: cycles a queue of models through ONE
 * offscreen viewer (load → snapshot → save → remove), so previews appear
 * without the user opening each model in Studio. The host mounts it only while
 * there is work and unmounts it on `onDone` (which disposes the viewer).
 *
 * Deliberately does NOT replay each model's saved `.vjson`. All the models share
 * one viewer and the contract exposes no `setSceneState`, so there is no way to
 * restore the pristine look between two models — one model's saved black
 * backdrop would silently become the next model's thumbnail. Every poster is
 * therefore captured against the viewer's default scene, which also makes the
 * Drive grid uniform. Studio's "Update thumbnail" is the path for capturing a
 * specific look; it snapshots exactly what is on screen.
 */
export default function PosterWorker({
  pending,
  onResult,
  onDone,
}: {
  pending: VaultModel[];
  /** Fires once per model. `posterUrl` is null when that model could not be re-rendered. */
  onResult: (id: string, posterUrl: string | null) => void;
  /** Every model in `pending` has been attempted — the host should unmount us. */
  onDone?: () => void;
}) {
  if (pending.length === 0) return null;
  return (
    <div aria-hidden style={{ position: "fixed", left: -10000, top: 0, width: 512, height: 512, pointerEvents: "none" }}>
      <ViewerHost mode="ring">
        {({ ready, client, iframe }) => (
          <Runner ready={ready} client={client} iframe={iframe} pending={pending} onResult={onResult} onDone={onDone} />
        )}
      </ViewerHost>
    </div>
  );
}

function Runner({
  ready,
  client,
  iframe,
  pending,
  onResult,
  onDone,
}: {
  ready: boolean;
  client: ViewerClient | null;
  iframe: React.ReactNode;
  pending: VaultModel[];
  onResult: (id: string, posterUrl: string | null) => void;
  onDone?: () => void;
}) {
  const done = useRef<Set<string>>(new Set());
  const busy = useRef(false);
  const drained = useRef(false);
  const [tick, setTick] = useState(0);
  // The host unmounts us from inside onDone/onResult, so the settle handlers
  // below can run against a torn-down component.
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; };
  }, []);

  useEffect(() => {
    if (!ready || !client || busy.current) return;
    const next = pending.find((m) => !done.current.has(m._id));
    if (!next) {
      // Report the drain exactly once — the effect re-runs on every `pending`
      // identity change, and a second onDone would restart a finished run.
      if (!drained.current) {
        drained.current = true;
        onDone?.();
      }
      return;
    }
    busy.current = true;
    done.current.add(next._id);

    (async () => {
      let modelId: string | null = null;
      let posterUrl: string | null = null;
      try {
        const { url } = await getModelGlbUrl(next._id);
        const native = ["3dm", "obj", "fbx", "stl"].includes(next.format);
        modelId = await client.ring.addModelUrl(url, { autoCenter: true, autoScale: native });
        await new Promise((r) => setTimeout(r, 2400)); // let it settle + render
        const blob = await client.ring.snapshot({ width: 512, height: 512, format: "png" });
        const dataUrl = await blobToDataURL(blob);
        posterUrl = (await saveModelPoster(next._id, dataUrl)).posterUrl;
      } catch {
        /* leave this one as it was; the user can still open it in Studio */
      } finally {
        if (modelId) {
          // Fire-and-forget, but a rejected RPC must not surface as an unhandled
          // rejection — a stale model left in the scene only costs memory.
          void client.ring.removeModel(modelId).catch(() => {});
        }
        busy.current = false;
        if (alive.current) {
          onResult(next._id, posterUrl);
          setTick((t) => t + 1); // re-run to pick the next pending model
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, client, pending, tick, onResult, onDone]);

  return <>{iframe}</>;
}
